// src/pages/business/search/AdvancedSearchPage.tsx
// Advanced Search with enhanced filters for business portal

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search, Filter, X, ChevronDown, ChevronUp, Loader2,
  Package, Tag, ShoppingCart, Users, Calendar, DollarSign,
  MapPin, Star, ArrowUpDown, Grid, List, SlidersHorizontal,
  Check, RotateCcw
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  stock_quantity: number;
  image_url?: string;
  business_name: string;
  created_at: string;
  rating?: number;
}

interface SearchResult {
  products: Product[];
  total: number;
  page: number;
  totalPages: number;
}

interface FilterState {
  categories: string[];
  minPrice?: number;
  maxPrice?: number;
  inStock: boolean;
  lowStock: boolean;
  outOfStock: boolean;
  minRating?: number;
  dateRange: 'all' | 'today' | 'week' | 'month' | 'year';
}

interface SortOption {
  field: 'name' | 'price' | 'created_at' | 'rating' | 'stock';
  order: 'asc' | 'desc';
}

const CATEGORIES = [
  'Electronics', 'Clothing', 'Food & Beverages', 'Health & Beauty',
  'Home & Garden', 'Sports', 'Books', 'Toys', 'Automotive', 'Other'
];

const AdvancedSearchPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult>({ products: [], total: 0, page: 1, totalPages: 0 });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showSortMenu, setShowSortMenu] = useState(false);

  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    minPrice: undefined,
    maxPrice: undefined,
    inStock: true,
    lowStock: false,
    outOfStock: false,
    minRating: undefined,
    dateRange: 'all'
  });

  const [sortOption, setSortOption] = useState<SortOption>({
    field: 'created_at',
    order: 'desc'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    if (searchQuery.trim() || Object.values(filters).some(v => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== false;
    })) {
      performSearch();
    }
  }, [searchQuery, filters, sortOption, currentPage]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const params: any = {
        q: searchQuery,
        page: currentPage,
        limit: itemsPerPage,
        sort: sortOption.field,
        order: sortOption.order,
      };

      if (filters.categories.length > 0) {
        params.categories = filters.categories.join(',');
      }
      if (filters.minPrice !== undefined) params.minPrice = filters.minPrice;
      if (filters.maxPrice !== undefined) params.maxPrice = filters.maxPrice;
      
      const stockStatuses: string[] = [];
      if (filters.inStock) stockStatuses.push('in_stock');
      if (filters.lowStock) stockStatuses.push('low_stock');
      if (filters.outOfStock) stockStatuses.push('out_of_stock');
      if (stockStatuses.length > 0) params.stockStatus = stockStatuses.join(',');

      if (filters.minRating !== undefined) params.minRating = filters.minRating;
      if (filters.dateRange !== 'all') params.dateRange = filters.dateRange;

      const res = await apiClient.get(`/businesses/${businessId}/products/search`, { params });
      setResults(res.data);
    } catch (error: any) {
      console.error('Search failed:', error);
      toastError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
    setCurrentPage(1);
  };

  const toggleStockFilter = (type: 'inStock' | 'lowStock' | 'outOfStock') => {
    setFilters(prev => ({ ...prev, [type]: !prev[type] }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      categories: [],
      minPrice: undefined,
      maxPrice: undefined,
      inStock: true,
      lowStock: false,
      outOfStock: false,
      minRating: undefined,
      dateRange: 'all'
    });
    setSortOption({ field: 'created_at', order: 'desc' });
    setCurrentPage(1);
    toastSuccess('Filters cleared');
  };

  const hasActiveFilters = () => {
    return (
      filters.categories.length > 0 ||
      filters.minPrice !== undefined ||
      filters.maxPrice !== undefined ||
      !filters.inStock ||
      filters.lowStock ||
      filters.outOfStock ||
      filters.minRating !== undefined ||
      filters.dateRange !== 'all'
    );
  };

  const activeFilterCount = () => {
    let count = 0;
    if (filters.categories.length > 0) count += filters.categories.length;
    if (filters.minPrice !== undefined) count++;
    if (filters.maxPrice !== undefined) count++;
    if (!filters.inStock) count++;
    if (filters.lowStock) count++;
    if (filters.outOfStock) count++;
    if (filters.minRating !== undefined) count++;
    if (filters.dateRange !== 'all') count++;
    return count;
  };

  const formatPrice = (price: number) => {
    return `KES ${price.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStockStatus = (product: Product) => {
    if (product.stock_quantity === 0) return { label: 'Out of Stock', color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' };
    if (product.stock_quantity < 10) return { label: 'Low Stock', color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/30' };
    return { label: 'In Stock', color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30' };
  };

  const sortOptions = [
    { label: 'Newest First', field: 'created_at' as const, order: 'desc' as const },
    { label: 'Oldest First', field: 'created_at' as const, order: 'asc' as const },
    { label: 'Price: Low to High', field: 'price' as const, order: 'asc' as const },
    { label: 'Price: High to Low', field: 'price' as const, order: 'desc' as const },
    { label: 'Name: A to Z', field: 'name' as const, order: 'asc' as const },
    { label: 'Name: Z to A', field: 'name' as const, order: 'desc' as const },
    { label: 'Highest Rated', field: 'rating' as const, order: 'desc' as const },
    { label: 'Most Stock', field: 'stock' as const, order: 'desc' as const },
  ];

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <Search size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Advanced Search
          </h1>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border border-light-border dark:border-dark-border hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
          >
            <SlidersHorizontal size={16} />
            Filters
            {activeFilterCount() > 0 && (
              <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                {activeFilterCount()}
              </span>
            )}
          </button>
        </div>

        {/* Search Input */}
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
          <input
            type="text"
            placeholder="Search products by name, description, category..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full pl-10 pr-10 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X size={16} className="text-light-subtle dark:text-dark-subtle" />
            </button>
          )}
        </div>

        {/* View & Sort Controls */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-light-subtle dark:text-dark-subtle">
              {results.total} results
            </span>
            {hasActiveFilters() && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-primary hover:bg-primary/10 transition-colors"
              >
                <RotateCcw size={12} />
                Clear all
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-light-border dark:border-dark-border hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              >
                <ArrowUpDown size={14} />
                {sortOptions.find(s => s.field === sortOption.field && s.order === sortOption.order)?.label}
                {showSortMenu ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showSortMenu && (
                <div className="absolute right-0 top-full mt-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg shadow-lg z-50 min-w-[180px]">
                  <div className="p-2">
                    {sortOptions.map((option) => (
                      <button
                        key={`${option.field}-${option.order}`}
                        onClick={() => {
                          setSortOption({ field: option.field, order: option.order });
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          sortOption.field === option.field && sortOption.order === option.order
                            ? 'bg-primary/10 text-primary font-semibold'
                            : 'text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-light-border dark:border-dark-border overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-primary text-white'
                    : 'bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle hover:bg-light-card dark:hover:bg-dark-card'
                }`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary text-white'
                    : 'bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle hover:bg-light-card dark:hover:bg-dark-card'
                }`}
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-4">
          <div className="max-w-5xl mx-auto space-y-4">
            {/* Categories */}
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(category => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      filters.categories.includes(category)
                        ? 'bg-primary text-white'
                        : 'bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text border border-light-border dark:border-dark-border hover:border-primary'
                    }`}
                  >
                    {category}
                    {filters.categories.includes(category) && (
                      <span className="ml-1 text-white/80">
                        <X size={12} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Price Range (KES)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.minPrice ?? ''}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, minPrice: e.target.value ? Number(e.target.value) : undefined }));
                    setCurrentPage(1);
                  }}
                  className="w-32 px-3 py-2 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="text-light-subtle dark:text-dark-subtle">to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.maxPrice ?? ''}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, maxPrice: e.target.value ? Number(e.target.value) : undefined }));
                    setCurrentPage(1);
                  }}
                  className="w-32 px-3 py-2 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            {/* Stock Status */}
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Stock Status
              </label>
              <div className="flex flex-wrap gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.inStock}
                    onChange={() => toggleStockFilter('inStock')}
                    className="w-4 h-4 rounded border-light-border dark:border-dark-border text-primary focus:ring-primary/20"
                  />
                  <span className="text-sm text-light-text dark:text-dark-text">In Stock</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.lowStock}
                    onChange={() => toggleStockFilter('lowStock')}
                    className="w-4 h-4 rounded border-light-border dark:border-dark-border text-primary focus:ring-primary/20"
                  />
                  <span className="text-sm text-light-text dark:text-dark-text">Low Stock</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.outOfStock}
                    onChange={() => toggleStockFilter('outOfStock')}
                    className="w-4 h-4 rounded border-light-border dark:border-dark-border text-primary focus:ring-primary/20"
                  />
                  <span className="text-sm text-light-text dark:text-dark-text">Out of Stock</span>
                </label>
              </div>
            </div>

            {/* Rating Filter */}
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Minimum Rating
              </label>
              <div className="flex gap-2">
                {[4, 3, 2, 1].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => {
                      setFilters(prev => ({ ...prev, minRating: prev.minRating === rating ? undefined : rating }));
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filters.minRating === rating
                        ? 'bg-primary text-white'
                        : 'bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text border border-light-border dark:border-dark-border hover:border-primary'
                    }`}
                  >
                    {Array.from({ length: rating }).map((_, i) => (
                      <Star key={i} size={12} className="inline" />
                    ))}
                    & Up
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Date Range
              </label>
              <div className="flex flex-wrap gap-2">
                {(['all', 'today', 'week', 'month', 'year'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => {
                      setFilters(prev => ({ ...prev, dateRange: range }));
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      filters.dateRange === range
                        ? 'bg-primary text-white'
                        : 'bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text border border-light-border dark:border-dark-border hover:border-primary'
                    }`}
                  >
                    {range.charAt(0).toUpperCase() + range.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Results */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-light-subtle dark:text-dark-subtle">Searching...</span>
          </div>
        ) : results.products.length === 0 ? (
          <div className="text-center py-12">
            <Search className="h-12 w-12 mx-auto text-light-subtle dark:text-dark-subtle mb-4" />
            <h3 className="text-lg font-semibold text-light-text dark:text-dark-text mb-2">No products found</h3>
            <p className="text-light-subtle dark:text-dark-subtle">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {results.products.map((product) => {
                  const stockStatus = getStockStatus(product);
                  return (
                    <div
                      key={product.id}
                      className="bg-light-card dark:bg-dark-card rounded-xl overflow-hidden border border-light-border dark:border-dark-border hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => navigate(`/business/${businessId}/products/${product.id}`)}
                    >
                      <div className="aspect-square bg-light-bg dark:bg-dark-bg relative">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-12 w-12 text-light-subtle dark:text-dark-subtle" />
                          </div>
                        )}
                        <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${stockStatus.bg} ${stockStatus.color}`}>
                          {stockStatus.label}
                        </span>
                        {product.rating && (
                          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 text-white text-xs">
                            <Star size={10} fill="currentColor" />
                            {product.rating.toFixed(1)}
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <h3 className="font-semibold text-light-text dark:text-dark-text text-sm truncate mb-1">
                          {product.name}
                        </h3>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle truncate mb-2">
                          {product.business_name}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-primary text-sm">
                            {formatPrice(product.price)}
                          </span>
                          {product.stock_quantity !== undefined && (
                            <span className="text-xs text-light-subtle dark:text-dark-subtle">
                              {product.stock_quantity} left
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3">
                {results.products.map((product) => {
                  const stockStatus = getStockStatus(product);
                  return (
                    <div
                      key={product.id}
                      className="bg-light-card dark:bg-dark-card rounded-xl overflow-hidden border border-light-border dark:border-dark-border hover:shadow-lg transition-shadow cursor-pointer flex"
                      onClick={() => navigate(`/business/${businessId}/products/${product.id}`)}
                    >
                      <div className="w-32 h-32 flex-shrink-0 bg-light-bg dark:bg-dark-bg relative">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-8 w-8 text-light-subtle dark:text-dark-subtle" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 p-3 flex flex-col justify-between">
                        <div>
                          <h3 className="font-semibold text-light-text dark:text-dark-text mb-1">
                            {product.name}
                          </h3>
                          <p className="text-xs text-light-subtle dark:text-dark-subtle mb-1">
                            {product.business_name}
                          </p>
                          {product.description && (
                            <p className="text-xs text-light-subtle dark:text-dark-subtle line-clamp-1">
                              {product.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-primary">
                              {formatPrice(product.price)}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stockStatus.bg} ${stockStatus.color}`}>
                              {stockStatus.label}
                            </span>
                            {product.rating && (
                              <div className="flex items-center gap-1 text-xs text-light-subtle dark:text-dark-subtle">
                                <Star size={10} fill="currentColor" />
                                {product.rating.toFixed(1)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {results.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg border border-light-border dark:border-dark-border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-light-subtle dark:text-dark-subtle">
                  Page {currentPage} of {results.totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(results.totalPages, p + 1))}
                  disabled={currentPage === results.totalPages}
                  className="px-4 py-2 rounded-lg border border-light-border dark:border-dark-border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdvancedSearchPage;
